import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface CashCountItem {
  denomination: number;
  quantity: number;
  total: number;
  type: "note" | "coin";
}

export interface PaymentMethodCount {
  paymentMethodId: string;
  paymentMethodName: string;
  amount: number;
}

// Brazilian currency denominations
export const DENOMINATIONS: CashCountItem[] = [
  // Notes
  { denomination: 200, quantity: 0, total: 0, type: "note" },
  { denomination: 100, quantity: 0, total: 0, type: "note" },
  { denomination: 50, quantity: 0, total: 0, type: "note" },
  { denomination: 20, quantity: 0, total: 0, type: "note" },
  { denomination: 10, quantity: 0, total: 0, type: "note" },
  { denomination: 5, quantity: 0, total: 0, type: "note" },
  { denomination: 2, quantity: 0, total: 0, type: "note" },
  // Coins
  { denomination: 1, quantity: 0, total: 0, type: "coin" },
  { denomination: 0.5, quantity: 0, total: 0, type: "coin" },
  { denomination: 0.25, quantity: 0, total: 0, type: "coin" },
  { denomination: 0.1, quantity: 0, total: 0, type: "coin" },
  { denomination: 0.05, quantity: 0, total: 0, type: "coin" },
];

export function useCashCount() {
  const { activeRestaurant } = useRestaurantContext();
  const [countItems, setCountItems] = useState<CashCountItem[]>(
    DENOMINATIONS.map((d) => ({ ...d }))
  );
  const [paymentMethodCounts, setPaymentMethodCounts] = useState<PaymentMethodCount[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const updateQuantity = useCallback((denomination: number, quantity: number) => {
    setCountItems((prev) =>
      prev.map((item) =>
        item.denomination === denomination
          ? { ...item, quantity, total: denomination * quantity }
          : item
      )
    );
  }, []);

  const updatePaymentMethodAmount = useCallback((paymentMethodId: string, amount: number) => {
    setPaymentMethodCounts((prev) => {
      const existing = prev.find((pmc) => pmc.paymentMethodId === paymentMethodId);
      if (existing) {
        return prev.map((pmc) =>
          pmc.paymentMethodId === paymentMethodId
            ? { ...pmc, amount }
            : pmc
        );
      } else {
        return [...prev, { paymentMethodId, paymentMethodName: "", amount }];
      }
    });
  }, []);

  const resetCount = useCallback(() => {
    setCountItems(DENOMINATIONS.map((d) => ({ ...d })));
    setPaymentMethodCounts([]);
  }, []);

  const getTotal = useCallback(() => {
    return countItems.reduce((sum, item) => sum + item.total, 0);
  }, [countItems]);

  const getPaymentMethodsTotal = useCallback(() => {
    return paymentMethodCounts.reduce((sum, pmc) => sum + pmc.amount, 0);
  }, [paymentMethodCounts]);

  const getNotes = useCallback(() => {
    return countItems.filter((item) => item.type === "note");
  }, [countItems]);

  const getCoins = useCallback(() => {
    return countItems.filter((item) => item.type === "coin");
  }, [countItems]);

  const saveCashCount = async (cashRegisterId: string) => {
    if (!activeRestaurant) return false;

    setIsLoading(true);
    try {
      // Save cash denominations
      const itemsToInsert = countItems
        .filter((item) => item.quantity > 0)
        .map((item) => ({
          cash_register_id: cashRegisterId,
          denomination: item.denomination,
          quantity: item.quantity,
          type: item.type,
        }));

      if (itemsToInsert.length > 0) {
        const { error } = await supabase
          .from("cash_count_details")
          .insert(itemsToInsert);

        if (error) throw error;
      }

      // Save payment method counts
      const paymentMethodsToInsert = paymentMethodCounts
        .filter((pmc) => pmc.amount > 0)
        .map((pmc) => ({
          cash_register_id: cashRegisterId,
          payment_method_id: pmc.paymentMethodId,
          counted_amount: pmc.amount,
        }));

      if (paymentMethodsToInsert.length > 0) {
        const { error } = await supabase
          .from("cash_register_payment_counts")
          .insert(paymentMethodsToInsert);

        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error("Error saving cash count:", error);
      toast.error("Erro ao salvar contagem");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const loadCashCount = async (cashRegisterId: string) => {
    setIsLoading(true);
    try {
      // Load cash denominations
      const { data, error } = await supabase
        .from("cash_count_details")
        .select("*")
        .eq("cash_register_id", cashRegisterId);

      if (error) throw error;

      if (data && data.length > 0) {
        setCountItems((prev) =>
          prev.map((item) => {
            const found = data.find(
              (d: any) => Number(d.denomination) === item.denomination
            );
            return found
              ? { ...item, quantity: found.quantity, total: Number(found.total) }
              : item;
          })
        );
      }

      // Load payment method counts
      const { data: paymentData, error: paymentError } = await supabase
        .from("cash_register_payment_counts")
        .select("*, payment_methods(name)")
        .eq("cash_register_id", cashRegisterId);

      if (paymentError) throw paymentError;

      if (paymentData && paymentData.length > 0) {
        setPaymentMethodCounts(
          paymentData.map((pd: any) => ({
            paymentMethodId: pd.payment_method_id,
            paymentMethodName: pd.payment_methods?.name || "",
            amount: Number(pd.counted_amount),
          }))
        );
      }
    } catch (error) {
      console.error("Error loading cash count:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    countItems,
    paymentMethodCounts,
    updateQuantity,
    updatePaymentMethodAmount,
    resetCount,
    getTotal,
    getPaymentMethodsTotal,
    getNotes,
    getCoins,
    saveCashCount,
    loadCashCount,
    isLoading,
  };
}
